import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowUp,
  FiCheckCircle,
  FiClock,
  FiLock,
  FiMail,
  FiMapPin,
} from "react-icons/fi";
import siteConfig from "../config/siteConfig";
import { formatAddress } from "./ContentPageElements";
import "../css/footer.scss";

const FALLBACK_FOOTER_LINKS = [
  {
    title: "Shop",
    links: [
      { label: "Shop all", to: "/shop" },
      { label: "Spices", to: "/shop?category=spices" },
      { label: "Blends", to: "/shop?category=blends" },
      { label: "Gift boxes", to: "/shop?category=gifts" },
    ],
  },
  {
    title: "Discover",
    links: [
      { label: "Our story", to: "/about" },
      { label: "Recipes & stories", to: "/journal" },
      { label: "Store locator", to: "/stores" },
      { label: "Gift cards", to: "/shop?category=gift-cards" },
    ],
  },
  {
    title: "Customer care",
    links: [
      { label: "Contact us", to: "/contact" },
      { label: "Shipping", to: "/shipping" },
      { label: "Returns", to: "/returns" },
      { label: "Frequently asked questions", to: "/faq" },
    ],
  },
  {
    title: "Policies",
    links: [
      { label: "Privacy", to: "/privacy" },
      { label: "Terms", to: "/terms" },
      { label: "Accessibility", to: "/accessibility" },
    ],
  },
];

function normalizeFooterLink(item, index) {
  if (typeof item === "string") {
    return { label: item, to: "/" };
  }

  return {
    label: item?.label || item?.title || item?.name || `Link ${index + 1}`,
    to: item?.to || item?.href || item?.path || "/",
  };
}

function normalizeFooterSections(footerLinks) {
  let sections = [];

  if (Array.isArray(footerLinks)) {
    sections = footerLinks;
  } else if (footerLinks && typeof footerLinks === "object") {
    sections = Object.entries(footerLinks).map(([key, value]) =>
      Array.isArray(value)
        ? { title: key, links: value }
        : {
            title: value?.title || key,
            links: value?.links || value?.items || [],
          }
    );
  }

  const normalizedSections = sections.map((section, index) => ({
    title:
      section?.title ||
      section?.heading ||
      section?.label ||
      `Links ${index + 1}`,
    links: (section?.links || section?.items || []).map((item, linkIndex) =>
      normalizeFooterLink(item, linkIndex)
    ),
  }));

  if (!normalizedSections.length) return FALLBACK_FOOTER_LINKS;

  const existingTitles = new Set(
    normalizedSections.map((section) => section.title.toLowerCase())
  );
  const supplementalSections = FALLBACK_FOOTER_LINKS.filter(
    (section) => !existingTitles.has(section.title.toLowerCase())
  );

  return [...normalizedSections, ...supplementalSections].slice(0, 4);
}

function isExternalLink(to) {
  return /^(https?:|mailto:)/i.test(to);
}

function FooterLink({ to, children, ...props }) {
  if (isExternalLink(to)) {
    const externalPage = /^https?:/i.test(to);
    return (
      <a
        href={to}
        rel={externalPage ? "noreferrer" : undefined}
        target={externalPage ? "_blank" : undefined}
        {...props}
      >
        {children}
      </a>
    );
  }

  return (
    <Link to={to} {...props}>
      {children}
    </Link>
  );
}

function Footer() {
  const [email, setEmail] = useState("");
  const [subscriptionComplete, setSubscriptionComplete] = useState(false);
  const footerSections = normalizeFooterSections(siteConfig.footerLinks);
  const contact = siteConfig.contact || {};
  const brandName = siteConfig.brandName || siteConfig.shortName || "";
  const shortName = siteConfig.shortName || brandName;
  const contactEmail = contact.email || contact.supportEmail;
  const rawAddress = contact.address || contact.location;
  const contactAddress = formatAddress(rawAddress);

  const handleNewsletterSubmit = (event) => {
    event.preventDefault();

    if (!event.currentTarget.checkValidity()) {
      event.currentTarget.reportValidity();
      return;
    }

    setSubscriptionComplete(true);
  };

  const toTop = () => {
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
  };

  return (
    <footer className="site-footer" role="contentinfo">
      <section
        aria-labelledby="footer-newsletter-title"
        className="site-footer__newsletter"
      >
        <div className="footer-newsletter__inner">
          <div className="footer-newsletter__copy">
            <span className="footer-newsletter__eyebrow">
              A little more flavor
            </span>
            <h2 id="footer-newsletter-title">
              {siteConfig.newsletter?.title || "Fresh ideas, delivered."}
            </h2>
            <p>
              {siteConfig.newsletter?.description ||
                "Seasonal recipes, pantry wisdom, and first tastes of new arrivals."}
            </p>
          </div>

          {subscriptionComplete ? (
            <div
              aria-live="polite"
              className="footer-newsletter__success"
              role="status"
            >
              <FiCheckCircle aria-hidden="true" />
              <div>
                <strong>You&rsquo;re on the list.</strong>
                <span>We&rsquo;ll send the good stuff to {email}.</span>
              </div>
              <button
                onClick={() => {
                  setSubscriptionComplete(false);
                  setEmail("");
                }}
                type="button"
              >
                Use another email
              </button>
            </div>
          ) : (
            <form
              className="footer-newsletter__form"
              onSubmit={handleNewsletterSubmit}
            >
              <label htmlFor="footer-newsletter-email">Email address</label>
              <div className="footer-newsletter__field">
                <FiMail aria-hidden="true" />
                <input
                  autoComplete="email"
                  id="footer-newsletter-email"
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  required
                  type="email"
                  value={email}
                />
                <button type="submit">Join the table</button>
              </div>
              <small>No clutter. Just useful, delicious notes.</small>
            </form>
          )}
        </div>
      </section>

      <div className="site-footer__body">
        <div className="site-footer__content">
          <section
            aria-label="Brand and contact information"
            className="footer-contact"
          >
            <Link
              aria-label={`${brandName} home`}
              className="footer-brand"
              to="/"
            >
              <span aria-hidden="true" className="footer-brand__badge">
                {shortName.charAt(0).toUpperCase()}
              </span>
              <span>
                <strong>{brandName}</strong>
                {siteConfig.tagline ? (
                  <small>{siteConfig.tagline}</small>
                ) : null}
              </span>
            </Link>
            {siteConfig.description ? (
              <p className="footer-contact__description">
                {siteConfig.description}
              </p>
            ) : null}

            <address className="footer-contact__details">
              {contactEmail ? (
                <a href={`mailto:${contactEmail}`}>
                  <FiMail aria-hidden="true" />
                  <span>{contactEmail}</span>
                </a>
              ) : null}
              {contactAddress ? (
                <span>
                  <FiMapPin aria-hidden="true" />
                  <span>{contactAddress}</span>
                </span>
              ) : null}
              {contact.hours ? (
                <span>
                  <FiClock aria-hidden="true" />
                  <span>{contact.hours}</span>
                </span>
              ) : null}
            </address>
          </section>

          <nav className="footer-links" aria-label="Footer navigation">
            {footerSections.map((section) => (
              <section className="footer-links__column" key={section.title}>
                <h2>{section.title}</h2>
                <ul>
                  {section.links.map((link) => (
                    <li key={`${link.label}-${link.to}`}>
                      <FooterLink to={link.to}>{link.label}</FooterLink>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </nav>
        </div>

        <div className="site-footer__bottom">
          <div className="site-footer__bottom-inner">
            <p>
              &copy; {new Date().getFullYear()} {brandName}. All rights
              reserved.
            </p>
            <p className="site-footer__secure">
              <FiLock aria-hidden="true" /> Secure checkout &middot; Major cards
              and digital wallets accepted
            </p>
            <button
              className="site-footer__to-top"
              onClick={toTop}
              type="button"
            >
              Back to top <FiArrowUp aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
